/**
 * AdminBehaviorPage - Phase 2C-4
 *
 * Admin page for behavioral analytics.
 * BSL Compliant: Factual data only, neutral colors.
 */

import { useState } from 'react';
import { useAdminPools } from '../../hooks/useAdminPools';
import { useBehaviorAnalytics } from '../../hooks/useBehaviorAnalytics';
import { AdminNav } from '../../components/admin';
import { Alert } from '../../components/common';
import {
  BehaviorFilters,
  BehaviorSummaryCard,
  UserBehaviorTable,
  DecisionTimeChart,
  StakeIntensityChart,
  LockPressureChart,
} from '../../components/behavior';

export function AdminBehaviorPage(): JSX.Element {
  const [selectedPoolId, setSelectedPoolId] = useState<string | null>(null);
  
  const { pools } = useAdminPools();
  const {
    summary,
    summaryLoading,
    summaryError,
    users,
    usersLoading,
    usersError,
    selectedUser,
    decisionTimeData,
    stakeIntensityData,
    lockPressureData,
    chartsLoading,
    selectUser,
    refetch,
  } = useBehaviorAnalytics(selectedPoolId);

  return (
    <div className="flex gap-6 p-6">
      {/* Sidebar */}
      <aside className="w-56 flex-shrink-0">
        <div className="sticky top-6">
          <h2 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-4">
            Admin
          </h2>
          <AdminNav />
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-slate-100">Behavior Analytics</h1>
          <p className="text-slate-400 mt-1">
            View behavioral patterns and decision metrics
          </p>
        </div>

        {/* Filters */}
        <BehaviorFilters
          pools={pools}
          selectedPoolId={selectedPoolId}
          onPoolChange={setSelectedPoolId}
          onRefresh={refetch}
          loading={summaryLoading || chartsLoading}
        />

        {/* No Pool Selected */}
        {!selectedPoolId && (
          <Alert variant="info">
            Select a pool to view behavioral analytics data.
          </Alert>
        )}

        {/* Error Display */}
        {summaryError && <Alert variant="error">{summaryError}</Alert>}

        {/* Analytics Content */}
        {selectedPoolId && (
          <>
            {/* Summary Card */}
            <BehaviorSummaryCard
              summary={summary}
              loading={summaryLoading}
              error={summaryError}
            />

            {/* Charts Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
              <DecisionTimeChart
                data={decisionTimeData}
                loading={chartsLoading}
              />
              <StakeIntensityChart
                data={stakeIntensityData}
                loading={chartsLoading}
              />
              <LockPressureChart
                data={lockPressureData}
                loading={chartsLoading}
              />
            </div>

            {/* Selected User Info */}
            {selectedUser && (
              <Alert variant="info">
                Viewing data for user: <strong>{selectedUser.user_id}</strong>
                {' '}| Events: {selectedUser.total_stake_events}
                {' '}| Accuracy: {(selectedUser.overall_accuracy * 100).toFixed(1)}%
              </Alert>
            )}

            {/* User Table */}
            <UserBehaviorTable
              users={users}
              loading={usersLoading}
              error={usersError}
              selectedUserId={selectedUser?.user_id}
              onSelectUser={selectUser}
            />
          </>
        )}
      </main>
    </div>
  );
}

export default AdminBehaviorPage;